import React, { createContext, useContext, useEffect, useReducer, ReactNode } from 'react';
import { LiveRegionAnnouncer, KeyboardShortcutManager } from '../utils/accessibility';

// Accessibility state interface
interface AccessibilityState {
  isKeyboardNavigationActive: boolean;
  isScreenReaderActive: boolean;
  prefersReducedMotion: boolean;
  prefersHighContrast: boolean;
  colorScheme: 'light' | 'dark' | 'auto';
  announcer: LiveRegionAnnouncer;
  shortcutManager: KeyboardShortcutManager;
}

// Accessibility actions
type AccessibilityAction =
  | { type: 'SET_KEYBOARD_NAVIGATION'; payload: boolean }
  | { type: 'SET_SCREEN_READER_ACTIVE'; payload: boolean }
  | { type: 'SET_REDUCED_MOTION'; payload: boolean }
  | { type: 'SET_HIGH_CONTRAST'; payload: boolean }
  | { type: 'SET_COLOR_SCHEME'; payload: 'light' | 'dark' | 'auto' };

// Initial state
const initialState: AccessibilityState = {
  isKeyboardNavigationActive: false,
  isScreenReaderActive: false,
  prefersReducedMotion: false,
  prefersHighContrast: false,
  colorScheme: 'auto',
  announcer: LiveRegionAnnouncer.getInstance(),
  shortcutManager: new KeyboardShortcutManager(),
};

// Reducer
function accessibilityReducer(
  state: AccessibilityState,
  action: AccessibilityAction
): AccessibilityState {
  switch (action.type) {
    case 'SET_KEYBOARD_NAVIGATION':
      return { ...state, isKeyboardNavigationActive: action.payload };
    case 'SET_SCREEN_READER_ACTIVE':
      return { ...state, isScreenReaderActive: action.payload };
    case 'SET_REDUCED_MOTION':
      return { ...state, prefersReducedMotion: action.payload };
    case 'SET_HIGH_CONTRAST':
      return { ...state, prefersHighContrast: action.payload };
    case 'SET_COLOR_SCHEME':
      return { ...state, colorScheme: action.payload };
    default:
      return state;
  }
}

// Context
const AccessibilityContext = createContext<{
  state: AccessibilityState;
  dispatch: React.Dispatch<AccessibilityAction>;
} | null>(null);

// Provider component
interface AccessibilityProviderProps {
  children: ReactNode;
}

export const AccessibilityProvider: React.FC<AccessibilityProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(accessibilityReducer, initialState);

  // Detect user preferences on mount
  useEffect(() => {
    // Check for reduced motion preference
    try {
      const reducedMotionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
      if (reducedMotionQuery) {
        dispatch({ type: 'SET_REDUCED_MOTION', payload: reducedMotionQuery.matches });

        const handleReducedMotionChange = (e: MediaQueryListEvent) => {
          dispatch({ type: 'SET_REDUCED_MOTION', payload: e.matches });
        };
        reducedMotionQuery.addEventListener('change', handleReducedMotionChange);

        // Store cleanup function
        const cleanupReducedMotion = () => {
          reducedMotionQuery.removeEventListener('change', handleReducedMotionChange);
        };

        // Check for high contrast preference
        const highContrastQuery = window.matchMedia?.('(prefers-contrast: high)');
        if (highContrastQuery) {
          dispatch({ type: 'SET_HIGH_CONTRAST', payload: highContrastQuery.matches });

          const handleHighContrastChange = (e: MediaQueryListEvent) => {
            dispatch({ type: 'SET_HIGH_CONTRAST', payload: e.matches });
          };
          highContrastQuery.addEventListener('change', handleHighContrastChange);

          // Check for color scheme preference
          const darkModeQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
          if (darkModeQuery) {
            const updateColorScheme = () => {
              if (darkModeQuery.matches) {
                dispatch({ type: 'SET_COLOR_SCHEME', payload: 'dark' });
              } else {
                dispatch({ type: 'SET_COLOR_SCHEME', payload: 'light' });
              }
            };
            updateColorScheme();

            const handleColorSchemeChange = () => {
              updateColorScheme();
            };
            darkModeQuery.addEventListener('change', handleColorSchemeChange);

            // Cleanup
            return () => {
              cleanupReducedMotion();
              highContrastQuery.removeEventListener('change', handleHighContrastChange);
              darkModeQuery.removeEventListener('change', handleColorSchemeChange);
            };
          }

          // Cleanup without dark mode query
          return () => {
            cleanupReducedMotion();
            highContrastQuery.removeEventListener('change', handleHighContrastChange);
          };
        }

        // Cleanup with only reduced motion
        return cleanupReducedMotion;
      }
    } catch (error) {
      console.warn('matchMedia not supported:', error);
    }

    // Detect screen reader usage
    const detectScreenReader = () => {
      try {
        // Check for common screen reader indicators
        const hasScreenReader = 
          (typeof navigator !== 'undefined' && (
            navigator.userAgent.includes('NVDA') ||
            navigator.userAgent.includes('JAWS') ||
            navigator.userAgent.includes('VoiceOver')
          )) ||
          (typeof window !== 'undefined' && window.speechSynthesis !== undefined);
        
        dispatch({ type: 'SET_SCREEN_READER_ACTIVE', payload: hasScreenReader });
      } catch (error) {
        console.warn('Screen reader detection failed:', error);
        dispatch({ type: 'SET_SCREEN_READER_ACTIVE', payload: false });
      }
    };
    detectScreenReader();

    // Return empty cleanup if no media queries are supported
    return () => {};
  }, []);

  // Handle keyboard navigation detection
  useEffect(() => {
    let keyboardUsed = false;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        keyboardUsed = true;
        dispatch({ type: 'SET_KEYBOARD_NAVIGATION', payload: true });
        document.body.classList.add('keyboard-navigation-active');
      }

      // Handle global keyboard shortcuts
      state.shortcutManager.handleKeyDown(e);
    };

    const handleMouseDown = () => {
      if (keyboardUsed) {
        keyboardUsed = false;
        dispatch({ type: 'SET_KEYBOARD_NAVIGATION', payload: false });
        document.body.classList.remove('keyboard-navigation-active');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [state.shortcutManager]);

  // Apply accessibility classes to body
  useEffect(() => {
    const body = document.body;
    
    // Apply classes based on preferences
    body.classList.toggle('reduced-motion', state.prefersReducedMotion);
    body.classList.toggle('high-contrast', state.prefersHighContrast);
    body.classList.toggle('screen-reader-active', state.isScreenReaderActive);
    body.classList.toggle('keyboard-navigation-active', state.isKeyboardNavigationActive);
    
    // Apply color scheme
    body.setAttribute('data-color-scheme', state.colorScheme);
  }, [
    state.prefersReducedMotion,
    state.prefersHighContrast,
    state.isScreenReaderActive,
    state.isKeyboardNavigationActive,
    state.colorScheme,
  ]);

  return (
    <AccessibilityContext.Provider value={{ state, dispatch }}>
      {children}
    </AccessibilityContext.Provider>
  );
};

// Hook to use accessibility context
export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
};

// Custom hooks for specific accessibility features
export const useAnnouncer = () => {
  const { state } = useAccessibility();
  return state.announcer;
};

export const useKeyboardShortcuts = () => {
  const { state } = useAccessibility();
  return state.shortcutManager;
};

export const useScreenReader = () => {
  const { state } = useAccessibility();
  return {
    isActive: state.isScreenReaderActive,
    announcer: state.announcer,
  };
};

export const useKeyboardNavigation = () => {
  const { state } = useAccessibility();
  return {
    isActive: state.isKeyboardNavigationActive,
    shortcutManager: state.shortcutManager,
  };
};